import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Hexagon, MapPin, Award, ArrowUpRight } from "lucide-react";

const pub = axios.create({ baseURL: API });

export default function FeaturedGallery() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    pub.get("/public/featured")
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  }, []);

  if (items === null) {
    return (
      <section className="py-16 bg-stone-50/60 border-y border-stone-100">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-sm text-stone-400">Loading featured apiaries…</p>
        </div>
      </section>
    );
  }
  if (items.length === 0) return null;

  return (
    <section className="py-20 bg-stone-50/60 border-y border-stone-100" data-testid="featured-gallery">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-end justify-between gap-4 mb-10 flex-wrap">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.25em] text-amber-700">Hive of the week</div>
            <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight mt-2">Peek inside real apiaries</h2>
            <p className="text-stone-600 mt-2 max-w-lg">Hand-picked from the community — opt-in public shares from beekeepers around the world.</p>
          </div>
          <Link to="/register" className="text-sm text-amber-700 hover:underline inline-flex items-center gap-1 group" data-testid="featured-cta-link">
            Get your own apiary listed <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((a) => (
            <li key={a.slug} data-testid={`featured-card-${a.slug}`}>
              <Link to={`/share/${a.slug}`} className="block bg-white border border-stone-200 hover:border-amber-300 rounded-2xl p-6 transition-all hover:shadow-sm group">
                <div className="flex items-center gap-3 mb-4">
                  {a.owner_avatar ? (
                    <img src={a.owner_avatar} alt="" className="w-12 h-12 rounded-full object-cover border border-stone-200" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-800 grid place-items-center font-display font-semibold text-lg">
                      {(a.owner_name || "?")[0]?.toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-lg font-medium truncate">{a.owner_name}</div>
                    {a.location && (
                      <div className="text-xs text-stone-500 flex items-center gap-1 truncate">
                        <MapPin className="w-3 h-3 shrink-0" /> {a.location}
                      </div>
                    )}
                  </div>
                </div>
                {a.bio && <p className="text-sm text-stone-600 line-clamp-2 mb-4">{a.bio}</p>}
                <div className="flex items-center justify-between text-xs text-stone-500 pt-3 border-t border-stone-100">
                  <span className="inline-flex items-center gap-1">
                    <Hexagon className="w-3.5 h-3.5 text-yellow-500" /> {a.hives_count} hive{a.hives_count === 1 ? "" : "s"}
                  </span>
                  {a.badges_count > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <Award className="w-3.5 h-3.5 text-amber-600" /> {a.badges_count} badge{a.badges_count === 1 ? "" : "s"}
                    </span>
                  )}
                  <span className="text-amber-700 font-medium opacity-0 group-hover:opacity-100 transition-opacity">View →</span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
